import { useRef, useState, useEffect } from 'react'
import {
  Dialog, DialogContent, DialogTitle, Divider,
  Typography, Box, List, ListItem, ListItemText, Radio, RadioGroup,
  FormControlLabel, CircularProgress, LinearProgress, IconButton,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import taapi from '../../services/taapi'
import { addToast } from '../../services/Toasts'
import type { BatchProcessUploadDocWebResult } from '../../services/KnowledgeBaseService'
import { FS_SM, FS_XS, ICON_SM } from '../../common/theme'
import { FileTypeIcon } from '../../comps/FileTypeIcon'
import { FancyButton } from '../../comps/FancyButtons'
import { TaDropZone, type TaDropZoneHandle } from '../../comps/TaDropZone'

const ACCEPTED = '.pdf,.docx,.doc,.xls,.xlsx,.csv,.txt,.rtf'
const BATCH_SIZE = 10

function hasExcel(files: File[]): boolean {
  return files.some(f => /\.(xls|xlsx|csv)$/i.test(f.name))
}

interface KbAddFilesModalProps {
  open: boolean
  dirsha?: string
  initialFiles?: File[]
  existingNames?: string[]
  onClose: () => void
  onUploaded: (files: File[], taskshalist: string[]) => void
}

export default function KbAddFilesModal({ open, dirsha, initialFiles, existingNames, onClose, onUploaded }: KbAddFilesModalProps) {
  const cancelledRef = useRef(false)
  const dropZoneRef = useRef<TaDropZoneHandle>(null)
  const [files, setFiles] = useState<File[]>([])
  const [uploadedCount, setUploadedCount] = useState(0)
  const [xlsOpt, setXlsOpt] = useState<'pages' | 'datarows'>('pages')
  const [uploading, setUploading] = useState(false)
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null)

  useEffect(() => {
    if (open) {
      setFiles(initialFiles && initialFiles.length > 0 ? initialFiles : [])
      setUploadedCount(0)
    }
  }, [open])

  const addFiles = (incoming: FileList | null) => {
    if (!incoming || incoming.length === 0) return
    setFiles(prev => {
      const names = new Set(prev.map(f => f.name))
      const next = Array.from(incoming).filter(f => !names.has(f.name))
      return [...prev, ...next]
    })
  }

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleClose = () => {
    cancelledRef.current = true
    setFiles([])
    setUploadedCount(0)
    setUploading(false)
    setBatchProgress(null)
    onClose()
  }

  const uploadBatch = async (batch: File[]): Promise<string[]> => {
    const fd = new FormData()
    batch.forEach(f => {
      fd.append('doc', f)
      fd.append('dirsha', dirsha ?? '')
      fd.append('projectsha', '')
    })
    fd.append('splitdoc', 'false')
    fd.append('buyer', 'false')
    if (hasExcel(batch)) fd.append('xlsoptval', xlsOpt)
    const result = (await taapi.postForm('/api2/kbupload2', fd)) as BatchProcessUploadDocWebResult | undefined
    if (!result || result.success === false) {
      throw new Error(result?.msg || 'Upload failed')
    }
    if (result.oversize && result.oversizelist && result.oversizelist.length > 0) {
      addToast('warn', `Skipped (too large): ${result.oversizelist.join(', ')}`)
    }
    return result.taskshalist ?? []
  }

  const handleUpload = async () => {
    if (files.length === 0) return
    cancelledRef.current = false
    setUploading(true)
    setUploadedCount(0)

    const existingSet = new Set((existingNames ?? []).map(n => n.toLowerCase()))
    const dupes = files.filter(f => existingSet.has(f.name.toLowerCase()))
    const allFiles = files.filter(f => !existingSet.has(f.name.toLowerCase()))

    if (dupes.length > 0) {
      addToast('warn', `${dupes.length} duplicate${dupes.length !== 1 ? 's' : ''} ignored`)
    }
    if (allFiles.length === 0) {
      setUploading(false)
      onClose()
      return
    }
    const batches: File[][] = []
    for (let i = 0; i < allFiles.length; i += BATCH_SIZE) {
      batches.push(allFiles.slice(i, i + BATCH_SIZE))
    }

    let hadError = false
    try {
      if (batches.length > 1) setBatchProgress({ current: 0, total: batches.length })

      for (let i = 0; i < batches.length; i++) {
        if (cancelledRef.current) break
        if (batches.length > 1) setBatchProgress({ current: i + 1, total: batches.length })

        try {
          const shas = await uploadBatch(batches[i])
          if (!cancelledRef.current) {
            const batchNames = new Set(batches[i].map(f => f.name))
            setFiles(prev => prev.filter(f => !batchNames.has(f.name)))
            setUploadedCount(prev => prev + batches[i].length)
            onUploaded(batches[i], shas)
          }
        } catch (err) {
          hadError = true
          const msg = err instanceof Error && err.message ? err.message : 'Upload failed'
          addToast('error', msg)
          break
        }
      }

      if (!cancelledRef.current && !hadError) {
        onClose()
      }
    } finally {
      setUploading(false)
      setBatchProgress(null)
      // On a clean run we close the modal and these reset on the next open via
      // the `useEffect([open])` initializer. On error we keep the failed files
      // staged so the user can retry.
      if (!hadError && !cancelledRef.current) {
        setFiles([])
        setUploadedCount(0)
      }
      if (!cancelledRef.current) cancelledRef.current = false
    }
  }

  const isBatching = batchProgress !== null
  const batchPct = isBatching ? Math.round((batchProgress.current / batchProgress.total) * 100) : 0
  const totalCount = files.length + uploadedCount

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth
      PaperProps={{ 'data-testid': 'kbam_dlg' } as any}>
      <DialogTitle sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
          <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>Add Files</Typography>
          {totalCount > 0 && (
            <Typography component="span" variant="caption" data-testid="kbam_filecount"
              sx={{ opacity: 0.55, fontSize: FS_XS }}>
              {totalCount} {totalCount === 1 ? 'file' : 'files'}
              {uploadedCount > 0 && ` · ${uploadedCount} uploaded`}
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: 2.5, pt: 2, pb: 2 }}>

        {/* Drop zone - always mounted (so the "Add more files" button below
            can reuse its file input) but the dashed band is hidden once
            files are staged or an upload is in flight. */}
        <Box sx={{ mb: files.length === 0 && !uploading ? 1.5 : 0 }}>
          <TaDropZone
            handleRef={dropZoneRef}
            testId="kbam_dropzone"
            inputTestId="kbam_fileinput"
            accept={ACCEPTED}
            multiple
            onFiles={addFiles}
            showZone={files.length === 0 && !uploading}
            label="Drop files here or click to browse"
            subLabel="PDF, DOCX, XLS, XLSX, CSV, TXT"
            sx={{ py: 2.5 }}
          />
        </Box>

        {/* File list */}
        {files.length > 0 && (
          <>
            <List dense disablePadding data-testid="kbam_filelist" sx={{ mb: 1, maxHeight: 200, overflowY: 'auto' }}>
              {files.map((f, i) => (
                <ListItem
                  key={i}
                  data-testid="kbam_file_row"
                  data-file-name={f.name}
                  disablePadding
                  sx={{ py: 0.25 }}
                  secondaryAction={!uploading && (
                    <IconButton size="small" edge="end" data-testid="kbam_file_remove_btn"
                      onClick={() => removeFile(i)} sx={{ p: 0.25 }}>
                      <CloseIcon sx={{ fontSize: ICON_SM }} />
                    </IconButton>
                  )}
                >
                  <Box sx={{ mr: 0.75, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                    <FileTypeIcon name={f.name} />
                  </Box>
                  <ListItemText
                    primary={f.name}
                    primaryTypographyProps={{ sx: { fontSize: FS_SM, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pr: 3 } }}
                  />
                </ListItem>
              ))}
            </List>

            {/* Add more button */}
            {!uploading && (
              <Box
                data-testid="kbam_addmore_btn"
                onClick={() => dropZoneRef.current?.openPicker()}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  cursor: 'pointer', mb: 1.5, px: 0.5, py: 0.5,
                  borderRadius: 1, width: 'fit-content',
                  color: 'primary.main', opacity: 0.75,
                  '&:hover': { opacity: 1 },
                }}
              >
                <AddIcon sx={{ fontSize: ICON_SM }} />
                <Typography variant="caption" sx={{ fontSize: FS_XS, fontWeight: 500 }}>Add more files</Typography>
              </Box>
            )}
          </>
        )}

        {/* Uploaded batch feedback */}
        {uploadedCount > 0 && (
          <Box data-testid="kbam_uploaded_summary"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, opacity: 0.7 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: ICON_SM, color: 'success.main' }} />
            <Typography variant="caption" sx={{ fontSize: FS_XS }}>
              {uploadedCount} {uploadedCount === 1 ? 'file' : 'files'} uploaded
            </Typography>
          </Box>
        )}

        {/* XLS option */}
        {files.length > 0 && hasExcel(files) && (
          <Box data-testid="kbam_xlsopt" sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 500, opacity: 0.7, display: 'block', mb: 0.5 }}>
              Process spreadsheets as:
            </Typography>
            <RadioGroup row value={xlsOpt} onChange={e => setXlsOpt(e.target.value as 'pages' | 'datarows')}>
              <FormControlLabel value="pages" control={<Radio size="small" inputProps={{ 'data-testid': 'kbam_xlsopt_pages' } as any} />}
                label={<Typography variant="caption" sx={{ fontSize: FS_SM }}>Text Pages</Typography>} />
              <FormControlLabel value="datarows" control={<Radio size="small" inputProps={{ 'data-testid': 'kbam_xlsopt_datarows' } as any} />}
                label={<Typography variant="caption" sx={{ fontSize: FS_SM }}>Data Rows</Typography>} />
            </RadioGroup>
          </Box>
        )}

        {/* Batch progress */}
        {isBatching && (
          <Box data-testid="kbam_batch_progress" sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ opacity: 0.7, fontSize: FS_XS }}>
                Uploading batch {batchProgress.current} of {batchProgress.total}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.5, fontSize: FS_XS }}>{batchPct}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={batchPct} sx={{ borderRadius: 1 }} />
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
          <FancyButton testId="kbam_cancel_btn" onClick={handleClose}>Cancel</FancyButton>
          {uploading && !isBatching
            ? <CircularProgress data-testid="kbam_uploading_spinner" size={20} />
            : !uploading && (
              <FancyButton
                solid
                disabled={files.length === 0}
                onClick={handleUpload}
              >
                <Box component="span" data-testid="kbam_upload_btn">Upload</Box>
              </FancyButton>
            )
          }
        </Box>
      </DialogContent>
    </Dialog>
  )
}
